import { config } from '../config';

// ============================================================================
// SYSTEM PROMPTS
// ============================================================================

const PATIENT_SYSTEM_PROMPT = `
You are PulseNote Patient Care — a friendly, empathetic medical assistant.

RULES:
- ALWAYS respond in clear, simple English only. Never mix languages.
- DO NOT give definitive diagnoses. Recommend the right TYPE of specialist.
- Be warm, reassuring, and easy to understand for a non-medical person.
- Keep responses concise (under 200 words).

FORMAT your response like this:
**What this might be:** [plain-English explanation]
**Who to see:** [type of specialist]
**What to do now:** [1-3 simple action steps]

SAFETY: If symptoms are life-threatening (chest pain + sweating, trouble breathing,
stroke signs, loss of consciousness), start with: ⚠️ URGENT — Call emergency services immediately.
`;

const DOCTOR_SYSTEM_PROMPT = `
You are Dr. Maverick, a clinical AI assistant embedded in PulseNote.

RULES:
- ALWAYS respond in English only, using professional medical terminology.
- Base analysis ONLY on the patient data provided. Never invent history or medications.
- Flag red flags and drug interactions explicitly.
- Keep responses structured and scannable for a busy clinician.

FORMAT your response using this structure:
**Clinical Summary:** [1-2 sentence overview of the patient's situation]
**Assessment:** [key findings, patterns, concerns from the history]
**Differential Diagnosis:** [ranked list if applicable]
**Recommended Actions:** [numbered list: investigations, treatments, referrals]
**Red Flags:** [anything requiring immediate attention, or "None identified"]
`;

// ============================================================================
// HELPER
// ============================================================================

const normalizeBanglish = (text) => {
    const mappings = {
        "matha": "matha (HEAD)", "buk": "buk (CHEST)", "pet": "pet (STOMACH)",
        "jor": "jor (FEVER)", "thanda": "thanda (COLD)"
    };
    let clean = text.toLowerCase();
    Object.keys(mappings).forEach(k => {
        clean = clean.replace(new RegExp(`\\b${k}\\b`, 'gi'), mappings[k]);
    });
    return clean;
};

/**
 * Strip Bengali (and other non-ASCII) characters from a string,
 * keeping only the parenthesised English translations.
 * e.g. "টাইফয়েড জ্বর (Typhoid fever)" → "Typhoid fever"
 */
const stripBengali = (text) => {
    if (!text) return text;
    // Bengali Unicode block: U+0980–U+09FF
    // "Bengali text (English translation)" → "English translation"
    return text
        .replace(/[\u0980-\u09FF]+\s*\(([^)]+)\)/g, '$1')  // "বাংলা (English)" → "English"
        .replace(/[\u0980-\u09FF]+/g, '')                    // remove leftover Bengali
        .replace(/\s{2,}/g, ' ')                             // collapse extra spaces
        .trim();
};

const fetchWithRetry = async (url, options, retries = 3, backoff = 1000) => {
    try {
        const response = await fetch(url, options);
        if (!response.ok) {
            const txt = await response.text();
            throw new Error(`Server responded with ${response.status}: ${txt}`);
        }
        return response;
    } catch (error) {
        if (retries > 0) {
            console.warn(`⚠️ Fetch failed. Retrying in ${backoff}ms...`, error);
            await new Promise(resolve => setTimeout(resolve, backoff));
            return fetchWithRetry(url, options, retries - 1, backoff * 2);
        }
        throw error;
    }
};

// ============================================================================
// PATIENT CHATBOT ENGINE  →  persona: "patient_bot"
// ============================================================================

export const analyzeSymptoms = async (chatHistory, language = 'en', userProfile = null) => {
    const lastUserMessage = normalizeBanglish(
        chatHistory[chatHistory.length - 1]?.content || ""
    );

    // Build context string from user profile if available
    let context = null;
    if (userProfile) {
        context = `Patient: ${userProfile.name}, Age: ${userProfile.age}, Allergies: ${userProfile.allergies || 'None'}`;
    }

    const payload = {
        query: lastUserMessage,
        persona: "patient_bot",
        context: context,
        max_tokens: 512,
        temperature: 0.3,
    };

    try {
        console.log("🚀 [Patient Bot] →", config.AI_API_URL);
        const response = await fetchWithRetry(config.AI_API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        const data = await response.json();
        return data.response;  // api_server.py returns { "response": "..." }

    } catch (error) {
        console.error("Patient AI Error:", error);
        return "⚠️ Patient AI Error: Unable to connect to backend. Please try again in a moment.";
    }
};

// ============================================================================
// DOCTOR CO-PILOT ENGINE  →  persona: "doctor_assistant"
// ============================================================================

export const analyzePatientHistory = async (chatHistory, patientData, visits, language = 'en') => {

    // 1. Build patient context string
    const recentVisits = (visits || []).map(v => {
        const dateStr = v.date?.seconds
            ? new Date(v.date.seconds * 1000).toLocaleDateString()
            : (v.date || 'N/A');
        const diagnosis = stripBengali(v.diagnosis || 'Unknown');
        const symptoms = stripBengali(v.symptoms || 'No symptoms listed');
        return `- ${dateStr}: ${diagnosis} (${symptoms})`;
    }).join('\n');

    const patientContextStr = `
PATIENT DETAILS:
- Name: ${patientData.name}
- Age: ${patientData.age} | Gender: ${patientData.gender}
- Blood Group: ${patientData.bloodType || patientData.bloodGroup || 'Unknown'}
- Condition: ${stripBengali(patientData.condition) || 'N/A'}
- Allergies: ${stripBengali(patientData.allergies) || 'NONE'}
- Current Meds: ${stripBengali(patientData.currentMedications) || 'None'}

RECENT VISITS:
${recentVisits || 'No recent visits recorded.'}
`.trim();

    const lastUserMessage = chatHistory[chatHistory.length - 1]?.content || "";

    const languageInstruction = language === 'bn'
        ? 'IMPORTANT: Respond entirely in Bengali (বাংলা). Use medical terms in Bengali where possible.'
        : 'IMPORTANT: Respond entirely in English only.';

    const payload = {
        query: lastUserMessage,
        persona: "doctor_assistant",
        context: `${patientContextStr}\n\n${languageInstruction}`,
        max_tokens: 600,
        temperature: 0.2,
    };

    try {
        console.log("🚀 [Doctor Bot] →", config.AI_API_URL);
        const response = await fetchWithRetry(config.AI_API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        const data = await response.json();
        return data.response;  // api_server.py returns { "response": "..." }

    } catch (error) {
        console.error("Doctor AI Error:", error);
        return `⚠️ System Error: Unable to analyze patient data. (${error.message})`;
    }
};

export const analyzePatientHistoryStream = async function* (chatHistory, patientData, visits, language = 'en') {
    const recentVisits = (visits || []).map(v => {
        const dateStr = v.date?.seconds
            ? new Date(v.date.seconds * 1000).toLocaleDateString()
            : (v.date || 'N/A');
        const diagnosis = stripBengali(v.diagnosis || 'Unknown');
        const symptoms = stripBengali(v.symptoms || 'No symptoms listed');
        return `- ${dateStr}: ${diagnosis} (${symptoms})`;
    }).join('\n');

    const patientContextStr = `
PATIENT DETAILS:
- Name: ${patientData.name}
- Age: ${patientData.age} | Gender: ${patientData.gender}
- Blood Group: ${patientData.bloodType || patientData.bloodGroup || 'Unknown'}
- Condition: ${stripBengali(patientData.condition) || 'N/A'}
- Allergies: ${stripBengali(patientData.allergies) || 'NONE'}
- Current Meds: ${stripBengali(patientData.currentMedications) || 'None'}

RECENT VISITS:
${recentVisits || 'No recent visits recorded.'}
`.trim();

    const lastUserMessage = chatHistory[chatHistory.length - 1]?.content || "";
    const languageInstruction = language === 'bn'
        ? 'IMPORTANT: Respond entirely in Bengali (বাংলা). Use medical terms in Bengali where possible.'
        : 'IMPORTANT: Respond entirely in English only.';

    const payload = {
        query: lastUserMessage,
        persona: "doctor_assistant",
        context: `${patientContextStr}\n\n${languageInstruction}`,
        max_tokens: 600,
        temperature: 0.2,
    };

    const streamUrl = config.AI_API_URL.replace('/query', '/stream_query');
    console.log("🌊 [Doctor Bot Stream] →", streamUrl);

    try {
        const response = await fetch(streamUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error(`Server responded with ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split('\n\n');
            buffer = lines.pop(); // Keep the last incomplete chunk in the buffer

            for (const line of lines) {
                if (line.trim().startsWith('data:')) {
                    const dataStr = line.replace('data:', '').trim();
                    if (dataStr === '[DONE]') break;
                    if (!dataStr) continue;

                    try {
                        const parsed = JSON.parse(dataStr);
                        if (parsed.error) throw new Error(parsed.error);
                        if (parsed.response !== undefined) yield parsed.response;
                    } catch (e) {
                        console.error('SSE JSON parse error:', e, dataStr);
                    }
                }
            }
        }
    } catch (error) {
        console.error("Doctor AI Stream Error:", error);
        yield `⚠️ System Error: Unable to stream analysis. (${error.message})`;
    }
};

// ============================================================================
// VOICE TRANSCRIPTION ENGINE
// ============================================================================

export const transcribeAudio = async (audioBlob, language = '') => {
    try {
        const formData = new FormData();
        formData.append('file', audioBlob);
        if (language) formData.append('language', language);

        const baseUrl = config.AI_BASE_URL;
        console.log(`🎙️ Sending transcription to: ${baseUrl}/transcribe`);

        const response = await fetchWithRetry(`${baseUrl}/transcribe`, {
            method: 'POST',
            body: formData
        });

        const data = await response.json();
        return data; // Expected { text: "..." }

    } catch (error) {
        console.error("Transcription Error:", error);
        throw error;
    }
};
